<template>
    <div class="relative overflow-hidden">
        <transition-group
            v-if="navButtons"
            name="fade"
            class="nav-buttons"
        >
            <Btn
                v-show="hasPrevious"
                key="prev-nav"
                :variant="BtnVariant.TEXT"
                :class="['left-0', navClass, 'navigation-button']"
                icon="chevron-left"
                :size="BtnSize.XXS"
                @click="previousSlides"
            />
            <Btn
                v-show="hasNext"
                key="next-nav"
                :variant="BtnVariant.TEXT"
                :class="['right-0', navClass, 'navigation-button']"
                icon="chevron-right"
                :size="BtnSize.XXS"
                @click="nextSlides"
            />
        </transition-group>

        <div
            ref="slides"
            v-drag-scroll
            :class="[
                'flex gap-x-3 scrollbars-none slides',
                {
                    'fade-left-edge': scrollDistance && !noEdgeFade && !noEdgeFadeLeft,
                    'fade-right-edge': hasNext && !noEdgeFade && !noEdgeFadeRight,
                },
            ]"
            :style="{
                paddingLeft: padding,
                paddingRight: padding,
                paddingTop: verticalPadding,
                paddingBottom: verticalPadding,
                scrollPadding: padding,
                '--sd-gap': gap,
            }"
            @scroll="onScroll"
        >
            <slot></slot>
        </div>
    </div>
</template>

<script>
import { BtnVariant, BtnSize } from '@components/Btn';

/**
 * Simple carousel component based on native scroll & snap behavior.
 * It's slide will currently only snap to the left edge.
 */
export default {
    name: 'Carousel',
    props: {
        navButtons: {
            type: Boolean,
            default: false,
        },
        /** specifies left and right padding of the whole carousel */
        padding: {
            type: String,
            default: '0px',
        },
        /** specifies top and bottom padding of the whole carousel */
        verticalPadding: {
            type: String,
            default: '0px',
        },
        /** gap between elements */
        gap: {
            type: String,
            default: null,
        },
        noEdgeFade: {
            type: Boolean,
            default: false,
        },
        noEdgeFadeLeft: {
            type: Boolean,
            default: false,
        },
        noEdgeFadeRight: {
            type: Boolean,
            default: false,
        },
        navClass: {
            type: undefined,
            default: '',
        },
        customGradientColor: {
            type: String,
            default: 'rgb(var(--colors-white)) 49.65%',
        },
    },
    data() {
        return {
            BtnVariant,
            BtnSize,
            activeIndex: null,
            scrollDistance: null,
        };
    },
    computed: {
        hasNext() {
            if (this.scrollDistance === null) return false;

            const { slides } = this.$refs;
            const remainingScrollDistance = slides.scrollWidth - this.scrollDistance - slides.offsetWidth;
            return remainingScrollDistance > 0;
        },
        hasPrevious() {
            return !!this.activeIndex;
        },
        gradientColor() {
            return this.customGradientColor;
        },
    },
    mounted() {
        this.updateScrollDistance();
        this.updateActiveIndex();
    },
    methods: {
        onScroll() {
            this.updateScrollDistance();
            this.updateActiveIndex();
        },
        updateScrollDistance() {
            this.scrollDistance = this.$refs.slides.scrollLeft;
        },
        updateActiveIndex() {
            const { slides } = this.$refs;
            this.activeIndex = Math.max(
                0,
                Array.from(slides.children).findIndex((el) => el.offsetLeft >= slides.scrollLeft) ?? 0,
            );
        },
        nextSlide() {
            if (!this.$refs.slides.children.length || this.$refs.slides.children.length <= this.activeIndex + 1) return;

            this.activeIndex += 1;
            this.scrollToSlideWithIndex(this.activeIndex);
        },
        previousSlide() {
            if (this.activeIndex === 0 || !this.$refs.slides.children.length) return;

            this.activeIndex -= 1;
            this.scrollToSlideWithIndex(this.activeIndex);
        },
        getSlideByIndex(index) {
            return this.$refs.slides?.children?.[index];
        },
        scrollToSlideWithIndex(index) {
            const scrollPadding = this.getScrollPadding();
            const slide = this.getSlideByIndex(index);

            if (!slide) return;

            const amount = slide.offsetLeft - this.$refs.slides.scrollLeft - scrollPadding;

            this.scrollCarousel(amount);
        },
        async scrollCarousel(amount) {
            this.scrollDistance = this.$refs.slides.scrollLeft + amount;

            await this.$nextTick();
            this.$refs.slides.scrollBy({
                left: amount,
                behavior: 'smooth',
            });
        },
        nextSlides() {
            const { slides } = this.$refs;
            const children = Array.from(slides.children);
            const maxOffset = slides.scrollLeft + slides.offsetWidth;
            const firstNotFullyVisibleIndex = children.findIndex((el) => el.offsetLeft + el.offsetWidth > maxOffset);

            // in case we cannot find anymore slides, scroll to the last one
            if (firstNotFullyVisibleIndex < 0) return this.scrollToSlideWithIndex(children.length - 1);

            return this.scrollToSlideWithIndex(firstNotFullyVisibleIndex);
        },
        previousSlides() {
            const { slides } = this.$refs;
            const children = Array.from(slides.children);
            const scrollPadding = this.getScrollPadding();

            const firstCurrentlyVisible = children.find((el) => el.offsetLeft >= slides.scrollLeft + scrollPadding / 2);
            // determine minimum offset so the visible slide will become not fully visible
            const minOffset = firstCurrentlyVisible.offsetLeft - slides.offsetWidth;

            const scrollToIndex = children.findIndex((el) => el.offsetLeft >= minOffset);

            if (scrollToIndex < 0) return this.scrollToSlideWithIndex(0);

            return this.scrollToSlideWithIndex(scrollToIndex);
        },
        getScrollPadding() {
            return parseInt(window.getComputedStyle(this.$refs.slides).paddingLeft, 10);
        },
    },
};
</script>

<style lang="scss" scoped>
.slides {
    @apply flex w-full h-full overflow-x-scroll;

    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;

    & > * {
        scroll-snap-align: start;
        flex-shrink: 0;

        &:not(:last-child) {
            margin-right: var(--sd-gap);
        }
    }

    img {
        user-select: none;
    }

    &::before,
    &::after {
        pointer-events: none;
        position: absolute;
        top: 0;
        height: 100%;
        content: '';
        min-width: 4rem;
        padding-right: inherit;
        opacity: 0;
        transition: all 0.3s ease;
        z-index: 1;
    }

    &::before {
        left: 0;
        background: linear-gradient(90deg, v-bind(gradientColor), rgba(255, 255, 255, 0) 92.66%);
    }

    &::after {
        right: 0;
        background: linear-gradient(-90deg, v-bind(gradientColor), rgba(255, 255, 255, 0) 92.66%);
    }

    &.fade-left-edge::before,
    &.fade-right-edge::after {
        opacity: 1;
    }
}

.navigation-button {
    @apply h-12 w-12 rounded-full bg-white border-slate-300 border shadow-md transform -translate-y-1/2;
    top: 50%;
    position: absolute;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    z-index: 2;
}
</style>
